interface CookieOptions {
    path?: string;
    secure?: boolean;
    sameSite?: boolean | 'none' | 'lax' | 'strict';
    maxAge?: number;
}

const isSecure =
    typeof window !== 'undefined' && window.location.protocol === 'https:';

const baseOptions: CookieOptions = {
    path: '/',
    secure: isSecure,
    sameSite: 'lax',
};

// Supertokens core default refresh token lifetime is 30 days.
// The refresh token is opaque (not a JWT), so we cannot decode its expiry.
export const REFRESH_TOKEN_MAX_AGE = 30 * 24 * 60 * 60; // 30 days

/**
 * Decode a JWT's payload without any external library.
 * JWT format: header.payload.signature (three base64url-encoded segments).
 * Returns the parsed JSON payload, or null if the token is malformed.
 */
const decodeJWTPayload = <T = Record<string, unknown>>(jwt: string): T | null => {
    try {
        const parts = jwt.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1];
        // base64url → base64 → JSON
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(json) as T;
    } catch {
        return null;
    }
};

/**
 * Compute the remaining seconds until a JWT expires.
 * Returns 0 if the token is already expired or cannot be decoded.
 */
export const getJWTRemainingSeconds = (jwt: string): number => {
    const payload = decodeJWTPayload<{ exp?: number }>(jwt);
    if (!payload?.exp) return 0;
    return Math.max(0, Math.floor(payload.exp - Date.now() / 1000));
};

/** Cookie options for removing cookies. Matches path and sameSite. */
export const removeCookieOptions: CookieOptions = {
    path: '/',
    sameSite: 'lax',
};

/**
 * Build cookie options for an access token, reading the JWT `exp` claim
 * to set maxAge dynamically. Falls back to 0 if the token is malformed
 * (the cookie won't be set, which is the correct behavior).
 */
export const getAccessTokenOptions = (accessToken: string): CookieOptions => ({
    ...baseOptions,
    maxAge: getJWTRemainingSeconds(accessToken),
});

/**
 * Build cookie options for a front token.
 * Front token lifetime is coupled to the access token by supertokens,
 * so we use the same expiry.
 */
export const getFrontTokenOptions = (accessToken: string): CookieOptions => ({
    ...baseOptions,
    maxAge: getJWTRemainingSeconds(accessToken),
});

/** Static cookie options for refreshToken (opaque, no JWT exp to decode). */
export const refreshTokenOptions: CookieOptions = {
    ...baseOptions,
    maxAge: REFRESH_TOKEN_MAX_AGE,
};

/**
 * Build a document.cookie string with proper attributes.
 * Used in places where react-cookie is unavailable (e.g., authenticatedFetch).
 */
export const buildCookieString = (
    name: string,
    value: string,
    maxAge: number
): string => {
    const parts = [`${name}=${value}`, `path=/`, `max-age=${maxAge}`];
    if (isSecure) {
        parts.push('secure');
    }
    parts.push('samesite=lax');
    return parts.join('; ');
};
